/**
 * shared/collision/geometry.ts — collision primitives in CoD space, ready for MeshCollisionWorld.
 *
 * OWNER: collision agent. Pure and deterministic (only + - * / and Math.sqrt on float64).
 *
 * Two primitive kinds, both clipped with Q3 CM_ClipBoxToBrush semantics (expanded planes,
 * SURFACE_CLIP_EPSILON) — see world.ts:
 *  - **Brushes**: convex sets of planes from the clipMap (`brushPlanes`). For an exact AABB sweep the
 *    Minkowski sum of box and brush needs, besides the brush's own sides, the 6 axial planes and the
 *    edge x axis "bevel" planes (q3map adds them at compile time, CM_AddBrushBevels). The Hijacked export
 *    already carries most of them; the few missing ones are added at build time so a box sweeping past a
 *    sloped edge is not stopped by the infinite extension of the sloped plane ("ghost" hits). This matches
 *    T6, which sweeps the player against the brush vertex hull with GJK (cbrush_t.verts; exact convex).
 *  - **Triangles** (clipMap patch/terrain/model-clip tris and static-model XModelCollSurf tris): a
 *    zero-thickness, double-sided convex brush whose planes are generated on the fly in world.ts: 6 axial,
 *    face, back face, and the 9 edge x axis bevels (the separating-axis set of AABB vs triangle) — the exact
 *    Minkowski sum, like T6's GJK against partition triangles (T5 bg_slidemove.cpp gjk_partition_t).
 *    ESTIMATE: T6 sweeps a polygon-cylinder hull (r 15, h 70, PT_AC_EPS 0.125) in the slide move; the
 *    CollisionWorld contract is an AABB, so the box is used everywhere (differs only at sloped edges/corners).
 */

/** Plane tolerance for building brush vertices / bevels (units). Q3 uses 0.1 in CM_AddFacetBevels. */
const BUILD_EPS = 0.01;
/** Normals closer than this (1 - dot) are treated as parallel (bevel de-duplication). */
const PARALLEL_EPS = 1e-5;

export interface BrushInput {
  /** Planes (outward normal xyz, dist; inside means n·p <= dist), CoD space. */
  planes: ArrayLike<number>;
  /** Surface flags per plane. */
  surfaceFlags: ArrayLike<number>;
  contents: number;
}

/** Growable builder for a set of brushes + triangles. */
export class CollisionGeometryBuilder {
  // brushes
  brushContents: number[] = [];
  brushFirstPlane: number[] = [];
  brushNumPlanes: number[] = [];
  brushBounds: number[] = [];
  planes: number[] = [];
  planeSurf: number[] = [];
  /** Source brush index (for debugging), -1 for synthetic. */
  brushSource: number[] = [];
  /** Submodel index (`*N`, 0 = world) of each brush / triangle; set `currentModel` before adding. */
  brushModel: number[] = [];
  triModel: number[] = [];
  currentModel = 0;
  // triangles
  triVerts: number[] = [];
  triContents: number[] = [];
  triSurf: number[] = [];
  triSource: number[] = [];
  /** Rejected inputs (degenerate triangles, empty brushes). */
  rejectedTris = 0;
  rejectedBrushes = 0;
  bevelPlanes = 0;
  axialPlanesAdded = 0;

  get numBrushes(): number { return this.brushContents.length; }
  get numTris(): number { return this.triContents.length; }

  /**
   * Add a convex brush. Computes its vertices from plane triples, adds missing axial planes and edge bevels.
   * `addBevels` = false keeps the given sides as they are (plus missing axials), i.e. the compiled plane
   * set only (loader BuildOptions.addBevels documents why the default adds them).
   * Plane order: the given sides first (clipMap order; the exported sides start with the 6 axial ones,
   * like Q3 BSP brushes), then added axial planes, then bevels. The order matters only for ties
   * (Q3 keeps the first plane with the largest enter fraction).
   */
  addBrush(b: BrushInput, source = -1, addBevels = true): boolean {
    const np = b.planes.length / 4;
    const P: number[] = [];
    const S: number[] = [];
    for (let i = 0; i < np; i++) {
      P.push(b.planes[i * 4], b.planes[i * 4 + 1], b.planes[i * 4 + 2], b.planes[i * 4 + 3]);
      S.push(b.surfaceFlags[i] >>> 0);
    }
    const verts = brushVertices(P);
    if (verts.length < 12) { this.rejectedBrushes++; return false; } // fewer than 4 vertices: no volume
    let mnx = Infinity, mny = Infinity, mnz = Infinity, mxx = -Infinity, mxy = -Infinity, mxz = -Infinity;
    for (let i = 0; i < verts.length; i += 3) {
      const x = verts[i], y = verts[i + 1], z = verts[i + 2];
      if (x < mnx) mnx = x; if (x > mxx) mxx = x;
      if (y < mny) mny = y; if (y > mxy) mxy = y;
      if (z < mnz) mnz = z; if (z > mxz) mxz = z;
    }
    // axial planes (exact ±1 normals) — present on every exported brush; added from the vertex bounds if
    // missing or not tight.
    const axial: [number, number, number, number][] = [
      [-1, 0, 0, -mnx], [1, 0, 0, mxx], [0, -1, 0, -mny], [0, 1, 0, mxy], [0, 0, -1, -mnz], [0, 0, 1, mxz],
    ];
    for (const a of axial) {
      if (hasParallel(P, a[0], a[1], a[2], a[3])) continue;
      P.push(a[0], a[1], a[2], a[3]);
      S.push(nearestSurf(P, S, np, a[0], a[1], a[2]));
      this.axialPlanesAdded++;
    }
    // edge bevels: for every brush edge e and axis a, the plane containing e and parallel to a, if it
    // supports the brush (all vertices behind it) — Q3 CM_AddFacetBevels / q3map AddBrushBevels.
    const nv = verts.length / 3;
    const nOrig = P.length / 4;
    for (let i = 0; addBevels && i < nv; i++) {
      for (let j = i + 1; j < nv; j++) {
        // edge iff the two vertices share >= 2 non-parallel planes
        const shared = sharedPlanes(P, nOrig, verts, i, j);
        if (shared < 0) continue;
        const ex = verts[j * 3] - verts[i * 3], ey = verts[j * 3 + 1] - verts[i * 3 + 1], ez = verts[j * 3 + 2] - verts[i * 3 + 2];
        for (let axis = 0; axis < 3; axis++) {
          // c = e x unit(axis)
          let cx: number, cy: number, cz: number;
          if (axis === 0) { cx = 0; cy = ez; cz = -ey; } else if (axis === 1) { cx = -ez; cy = 0; cz = ex; } else { cx = ey; cy = -ex; cz = 0; }
          const len = Math.sqrt(cx * cx + cy * cy + cz * cz);
          if (len < 1e-6) continue;
          cx /= len; cy /= len; cz /= len;
          for (let sgn = 0; sgn < 2; sgn++) {
            if (sgn === 1) { cx = -cx; cy = -cy; cz = -cz; }
            const d = cx * verts[i * 3] + cy * verts[i * 3 + 1] + cz * verts[i * 3 + 2];
            let ok = true;
            let dmax = d;
            for (let k = 0; k < nv; k++) {
              const dk = cx * verts[k * 3] + cy * verts[k * 3 + 1] + cz * verts[k * 3 + 2];
              if (dk > d + BUILD_EPS) { ok = false; break; }
              if (dk > dmax) dmax = dk;
            }
            if (!ok) continue;
            if (hasParallel(P, cx, cy, cz, dmax)) continue;
            P.push(cx, cy, cz, dmax);
            S.push(nearestSurf(P, S, np, cx, cy, cz));
            this.bevelPlanes++;
          }
        }
      }
    }
    this.brushFirstPlane.push(this.planeSurf.length);
    this.brushNumPlanes.push(P.length / 4);
    for (let i = 0; i < P.length; i++) this.planes.push(P[i]);
    for (let i = 0; i < S.length; i++) this.planeSurf.push(S[i]);
    this.brushContents.push(b.contents >>> 0);
    this.brushBounds.push(mnx, mny, mnz, mxx, mxy, mxz);
    this.brushSource.push(source);
    this.brushModel.push(this.currentModel);
    return true;
  }

  /** Axis-aligned box brush (planes ordered -x,+x,-y,+y,-z,+z like BoxWorld.clipBoxToAabb). */
  addBox(mins: ArrayLike<number>, maxs: ArrayLike<number>, contents: number, surfaceFlags = 0): boolean {
    return this.addBrush({
      planes: [-1, 0, 0, -mins[0], 1, 0, 0, maxs[0], 0, -1, 0, -mins[1], 0, 1, 0, maxs[1], 0, 0, -1, -mins[2], 0, 0, 1, maxs[2]],
      surfaceFlags: [surfaceFlags, surfaceFlags, surfaceFlags, surfaceFlags, surfaceFlags, surfaceFlags],
      contents,
    });
  }

  addTriangle(
    ax: number, ay: number, az: number, bx: number, by: number, bz: number, cx: number, cy: number, cz: number,
    contents: number, surfaceFlags: number, source = 1,
  ): boolean {
    const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
    const e2x = cx - ax, e2y = cy - ay, e2z = cz - az;
    const nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
    // degenerate (area < ~1e-4 sq units) triangles cannot be hit reliably; drop them
    if (nx * nx + ny * ny + nz * nz < 1e-8) { this.rejectedTris++; return false; }
    this.triVerts.push(ax, ay, az, bx, by, bz, cx, cy, cz);
    this.triContents.push(contents >>> 0);
    this.triSurf.push(surfaceFlags >>> 0);
    this.triSource.push(source);
    this.triModel.push(this.currentModel);
    return true;
  }
}

/**
 * True if P already has a plane parallel to n that is tight, i.e. touches the brush (dist <= support + eps).
 * A parallel plane that is looser than the support (a redundant side cut away by other sides) does not count:
 * the expanded sweep needs the tight plane, else the box would be stopped in empty space ("ghost" hit).
 */
function hasParallel(P: number[], nx: number, ny: number, nz: number, support: number): boolean {
  for (let k = 0; k < P.length; k += 4) {
    if (P[k] * nx + P[k + 1] * ny + P[k + 2] * nz > 1 - PARALLEL_EPS && P[k + 3] <= support + BUILD_EPS) return true;
  }
  return false;
}

/** Surface flags for a generated plane: those of the original side whose normal is closest. */
function nearestSurf(P: number[], S: number[], nOrig: number, nx: number, ny: number, nz: number): number {
  let best = -2, bs = 0;
  for (let k = 0; k < nOrig; k++) {
    const d = P[k * 4] * nx + P[k * 4 + 1] * ny + P[k * 4 + 2] * nz;
    if (d > best) { best = d; bs = S[k]; }
  }
  return bs;
}

/** Returns >= 0 if vertices i and j lie on two common non-parallel planes (i.e. form a brush edge). */
function sharedPlanes(P: number[], nPlanes: number, v: number[], i: number, j: number): number {
  let first = -1;
  for (let k = 0; k < nPlanes; k++) {
    const nx = P[k * 4], ny = P[k * 4 + 1], nz = P[k * 4 + 2], d = P[k * 4 + 3];
    const di = nx * v[i * 3] + ny * v[i * 3 + 1] + nz * v[i * 3 + 2] - d;
    if (di > BUILD_EPS || di < -BUILD_EPS) continue;
    const dj = nx * v[j * 3] + ny * v[j * 3 + 1] + nz * v[j * 3 + 2] - d;
    if (dj > BUILD_EPS || dj < -BUILD_EPS) continue;
    if (first < 0) { first = k; continue; }
    const dot = P[first * 4] * nx + P[first * 4 + 1] * ny + P[first * 4 + 2] * nz;
    if (dot < 1 - PARALLEL_EPS) return k;
  }
  return -1;
}

/**
 * Vertices of a convex brush: intersections of plane triples that lie inside every plane
 * (within BUILD_EPS), de-duplicated. Deterministic (fixed iteration order).
 */
export function brushVertices(P: ArrayLike<number>): number[] {
  const n = P.length / 4;
  const out: number[] = [];
  for (let a = 0; a < n; a++) {
    const ax = P[a * 4], ay = P[a * 4 + 1], az = P[a * 4 + 2], ad = P[a * 4 + 3];
    for (let b = a + 1; b < n; b++) {
      const bx = P[b * 4], by = P[b * 4 + 1], bz = P[b * 4 + 2], bd = P[b * 4 + 3];
      // n_b x n_c etc.
      for (let c = b + 1; c < n; c++) {
        const cx = P[c * 4], cy = P[c * 4 + 1], cz = P[c * 4 + 2], cd = P[c * 4 + 3];
        const bcx = by * cz - bz * cy, bcy = bz * cx - bx * cz, bcz = bx * cy - by * cx;
        const det = ax * bcx + ay * bcy + az * bcz;
        if (det > -1e-9 && det < 1e-9) continue;
        const cax = cy * az - cz * ay, cay = cz * ax - cx * az, caz = cx * ay - cy * ax;
        const abx = ay * bz - az * by, aby = az * bx - ax * bz, abz = ax * by - ay * bx;
        const px = (ad * bcx + bd * cax + cd * abx) / det;
        const py = (ad * bcy + bd * cay + cd * aby) / det;
        const pz = (ad * bcz + bd * caz + cd * abz) / det;
        let inside = true;
        for (let k = 0; k < n; k++) {
          if (P[k * 4] * px + P[k * 4 + 1] * py + P[k * 4 + 2] * pz - P[k * 4 + 3] > BUILD_EPS) { inside = false; break; }
        }
        if (!inside) continue;
        let dup = false;
        for (let k = 0; k < out.length; k += 3) {
          const dx = out[k] - px, dy = out[k + 1] - py, dz = out[k + 2] - pz;
          if (dx * dx + dy * dy + dz * dz < BUILD_EPS * BUILD_EPS) { dup = true; break; }
        }
        if (!dup) out.push(px, py, pz);
      }
    }
  }
  return out;
}
